import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { useRoles } from "@/context/RolesContext";
import { absUrl } from "@/lib/config";
import UserDocumentsModal from "@/components/UserDocumentsModal";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import BulkImportUsersDialog from "@/components/BulkImportUsersDialog";
import { Plus, Trash, Prohibit, CheckCircle, PencilSimple, IdentificationBadge, X, FileText, MagnifyingGlass, Rocket, DoorOpen, Key, ClipboardText, Upload } from "@phosphor-icons/react";

// Built-in role badge classes — dynamic (custom) roles fall back to their `color` field.
const BUILT_IN_ROLE_BG = {
  admin: "bg-[#002FA7] text-white",
  manager: "bg-[#FF3B30] text-white",
  hr: "bg-[#7C3AED] text-white",
  employee: "bg-[#0A0A0A] text-white",
};
export const roleBadgeStyle = (key, color) => {
  if (BUILT_IN_ROLE_BG[key]) return { className: BUILT_IN_ROLE_BG[key], style: {} };
  return { className: "text-white", style: { backgroundColor: color || "#0A0A0A" } };
};

function RoleChip({ roleKey, allRoles, size = "sm" }) {
  const r = allRoles.find(x => x.key === roleKey);
  const style = roleBadgeStyle(roleKey, r?.color);
  const label = r?.label || roleKey;
  const cls = size === "md" ? "text-[11px] px-2 py-0.5" : "text-[10px] px-1.5 py-0.5";
  return (
    <span
      className={`${cls} uppercase font-mono-mini ${style.className}`}
      style={style.style}
      data-testid={`role-chip-${roleKey}`}
    >
      {label}
    </span>
  );
}

function EditUserModal({ user: target, allUsers, onClose, onSaved, currentUser, can }) {
  const { roles: allRoles } = useRoles();
  const [name, setName] = useState(target.name || "");
  const [dept, setDept] = useState(target.department || "");
  const [pos, setPos] = useState(target.position || "");
  const [roles, setRoles] = useState(target.roles || [target.role]);
  const [leaveBalance, setLeaveBalance] = useState(target.leave_balance ?? 20);
  const [assignedHrIds, setAssignedHrIds] = useState(target.assigned_hr_ids || []);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const canEditRoles = can("manage_multi_role") && currentUser.roles?.includes("admin");
  const canEditBalance = can("manage_leave_balance");
  const canEditHr = can("assign_hr");
  const hrRoleKeys = allRoles.filter(r => r.flags?.is_hr_approver).map(r => r.key);
  const hrUsers = allUsers.filter(u => (u.roles || [u.role]).some(r => hrRoleKeys.includes(r)));

  const toggleRole = (r) => {
    setRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  };
  const toggleHr = (id) => {
    setAssignedHrIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const patch = { name, department: dept, position: pos };
      if (canEditRoles) {
        if (roles.length === 0) throw new Error("A user must have at least one role.");
        patch.roles = roles;
        patch.role = roles[0]; // primary display role
      }
      if (canEditBalance) patch.leave_balance = Number(leaveBalance);
      await api.patch(`/users/${target.id}`, patch);
      if (canEditHr) {
        await api.patch(`/users/${target.id}/assigned-hr`, { hr_ids: assignedHrIds });
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      setError(err.response ? formatAxiosError(err) : err.message);
    } finally { setSaving(false); }
  };

  // Show every role (sorted by order, then label) so admins can grant any custom role.
  const roleOptions = [...allRoles].sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.label.localeCompare(b.label));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="eyebrow">Directory</div>
            <h2 className="font-display text-xl font-bold">Edit {target.name}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={18} weight="bold" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-2">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-border px-3 py-2 text-sm" data-testid="edit-user-name" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Department</label>
              <input value={dept} onChange={(e) => setDept(e.target.value)} className="w-full border border-border px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2">Position</label>
            <input value={pos} onChange={(e) => setPos(e.target.value)} className="w-full border border-border px-3 py-2 text-sm" />
          </div>

          {canEditRoles && (
            <div>
              <label className="eyebrow block mb-2">Roles (a user can hold multiple)</label>
              <div className="grid grid-cols-2 gap-2">
                {roleOptions.map(r => {
                  const checked = roles.includes(r.key);
                  return (
                    <label key={r.key} className={`flex items-center gap-2 border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted ${checked ? "bg-muted" : ""}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleRole(r.key)} data-testid={`edit-role-${r.key}`} />
                      <RoleChip roleKey={r.key} allRoles={allRoles} />
                    </label>
                  );
                })}
              </div>
              <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">Primary role (first-listed) drives dashboard defaults.</div>
            </div>
          )}

          {canEditBalance && (
            <div>
              <label className="eyebrow block mb-2">Leave balance (days)</label>
              <input type="number" min="0" value={leaveBalance} onChange={(e) => setLeaveBalance(e.target.value)} className="w-full border border-border px-3 py-2 text-sm" data-testid="edit-leave-balance" />
            </div>
          )}

          {canEditHr && (
            <div>
              <label className="eyebrow mb-2 flex items-center gap-2"><IdentificationBadge size={12} weight="bold" /> Assigned HR</label>
              <div className="text-[10px] font-mono-mini text-muted-foreground mb-2">
                {assignedHrIds.length === 0
                  ? "Empty — falls back to every HR approving leaves."
                  : `Only these ${assignedHrIds.length} HR(s) will approve this employee's leaves.`}
              </div>
              <div className="border border-border max-h-40 overflow-y-auto">
                {hrUsers.length === 0 ? (
                  <div className="p-3 text-xs text-muted-foreground text-center">No HR users found.</div>
                ) : hrUsers.map(h => (
                  <label key={h.id} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0 hover:bg-muted cursor-pointer text-sm">
                    <input type="checkbox" checked={assignedHrIds.includes(h.id)} onChange={() => toggleHr(h.id)} data-testid={`assign-hr-${h.id}`} />
                    <span className="font-semibold">{h.name}</span>
                    <span className="text-muted-foreground text-xs ml-auto">{h.email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {error && <div className="text-xs text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 btn-primary disabled:opacity-50" data-testid="save-user-edit">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Directory() {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { roles: allRoles } = useRoles();
  const { confirm, alert } = useDialog();
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "", department: "", position: "" });
  const [error, setError] = useState("");

  const userRoles = user.roles || [user.role];
  const isAdmin = userRoles.includes("admin");
  const canCreate = can("create_users");
  const canBulkImport = can("bulk_import_users");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const canDisable = can("disable_users");
  const canDelete = can("delete_users");
  const canViewDocs = can("view_employee_documents");
  const canResetPw = userRoles.includes("admin") || userRoles.includes("hr");
  const canInitiateReview = can("initiate_performance_review");
  const [resetPwUser, setResetPwUser] = useState(null);
  const [reviewUser, setReviewUser] = useState(null);

  // Global search + offboarded toggle
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = no search active
  const [searching, setSearching] = useState(false);
  const [includeOffboarded, setIncludeOffboarded] = useState(true);
  const [docsUserId, setDocsUserId] = useState(null);
  const [statuses, setStatuses] = useState({}); // {uid: {onboarding_status, offboarding_status}}

  // Dynamic: a role is creatable by current user if any of their effective roles
  // is listed in the target role's `creatable_by` array. Admin can create anything.
  const allowedRoles = useMemo(() => {
    if (isAdmin) return allRoles.map(r => r.key);
    const mine = new Set(userRoles);
    return allRoles
      .filter(r => (r.creatable_by || []).some(k => mine.has(k)))
      .map(r => r.key);
  }, [allRoles, userRoles, isAdmin]);

  // Rank lookup from role docs — higher rank = more privileged. Admin=100.
  const rankOf = useMemo(() => {
    const m = {};
    allRoles.forEach(r => { m[r.key] = r.rank ?? 0; });
    return m;
  }, [allRoles]);

  const load = async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) { setError(formatAxiosError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Load onboarding/offboarding statuses for the visible users (via directory search endpoint)
  const loadStatuses = async () => {
    try {
      const r = await api.get("/directory/search", { params: { q: "", include_offboarded: true } });
      const map = {};
      (r.data.results || []).forEach(res => {
        map[res.user.id] = {
          onboarding_status: res.onboarding_status,
          offboarding_status: res.offboarding_status,
        };
      });
      setStatuses(map);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadStatuses(); }, [users.length]); // eslint-disable-line

  const doSearch = async () => {
    const q = searchQ.trim();
    if (!q) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const r = await api.get("/directory/search", { params: { q, include_offboarded: includeOffboarded } });
      setSearchResults(r.data.results || []);
    } catch (e) { setError(formatAxiosError(e)); }
    finally { setSearching(false); }
  };
  const clearSearch = () => { setSearchQ(""); setSearchResults(null); };

  const usersById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);

  const toggleDisabled = async (u) => {
    try {
      await api.patch(`/users/${u.id}/status`, { disabled: !u.disabled });
      load();
    } catch (err) { alert({ title: "Update failed", description: formatAxiosError(err), variant: "destructive" }); }
  };

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/users", form);
      setShowCreate(false);
      setForm({ name: "", email: "", password: "", role: "", department: "", position: "" });
      load();
    } catch (err) {
      setError(formatAxiosError(err));
    }
  };

  const remove = async (id) => {
    const u = usersById[id];
    const ok = await confirm({
      title: u ? `Delete "${u.name}"?` : "Delete user?",
      description: "The user account will be permanently removed. Related data (tasks, comments, attendance) remains tied to the user ID.",
      confirmText: "Delete user",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/users/${id}`);
      load();
    } catch (err) { alert({ title: "Delete failed", description: formatAxiosError(err), variant: "destructive" }); }
  };

  const canActOn = (u) => {
    if (u.id === user.id) return false;
    if (isAdmin) return true;
    const targetRoles = u.roles || [u.role];
    const targetTopRank = Math.max(...targetRoles.map(r => rankOf[r] || 0), 0);
    const myTopRank = Math.max(...userRoles.map(r => rankOf[r] || 0), 0);
    return targetTopRank <= myTopRank && !targetRoles.includes("admin");
  };

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">People / Directory</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Team directory</h1>
          <p className="text-sm text-muted-foreground mt-2">Everyone across the organization. Visible only to admins, managers and HR.</p>
        </div>
        {canCreate && allowedRoles.length > 0 && (
          <div className="flex items-center gap-2">
            {canBulkImport && (
              <button onClick={() => setShowBulkImport(true)} className="btn-secondary flex items-center gap-2" data-testid="bulk-import-button">
                <Upload size={16} weight="bold" /> Bulk import
              </button>
            )}
            <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2" data-testid="create-user-button">
              <Plus size={16} weight="bold" /> Add member
            </button>
          </div>
        )}
      </div>

      <div className="p-8 lg:p-12">
        {error && <div className="mb-4 text-xs text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}

        {/* Global search bar */}
        <div className="mb-6 border border-border p-4 flex items-center gap-3 flex-wrap" data-testid="directory-search-bar">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <MagnifyingGlass size={16} weight="bold" className="text-muted-foreground shrink-0" />
            <input
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") doSearch(); }}
              placeholder={canViewDocs ? "Search by name, email, department, position, or onboarding data (education, skills, employer)..." : "Search by name, email, department, position..."}
              className="flex-1 border border-border px-3 py-2 text-sm"
              data-testid="directory-search-input"
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" checked={includeOffboarded} onChange={e => setIncludeOffboarded(e.target.checked)} className="accent-[#002FA7]" data-testid="include-offboarded" />
            Include ex-employees
          </label>
          <button onClick={doSearch} disabled={searching} className="btn-primary px-4 py-2 flex items-center gap-1 disabled:opacity-40" data-testid="directory-search-button">
            <MagnifyingGlass size={12} weight="bold" /> {searching ? "Searching…" : "Search"}
          </button>
          {searchResults !== null && (
            <button onClick={clearSearch} className="border border-border px-3 py-2 text-xs hover:bg-muted flex items-center gap-1">
              <X size={12} weight="bold" /> Clear
            </button>
          )}
        </div>

        {searchResults !== null && (
          <div className="mb-3 text-xs text-muted-foreground">
            {searchResults.length} result(s) for &quot;{searchQ}&quot;
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 border border-border" data-testid="directory-grid">
          {(searchResults !== null ? searchResults.map(r => r.user) : users).map((u) => {
            const avatarUrl = u.avatar ? absUrl(u.avatar) : null;
            const acts = canActOn(u);
            const displayRoles = (u.roles && u.roles.length > 0) ? u.roles : [u.role];
            const hrNames = (u.assigned_hr_ids || []).map(id => usersById[id]?.name).filter(Boolean);
            const status = statuses[u.id] || {};
            const onbComplete = status.onboarding_status === "complete";
            const onbInProgress = status.onboarding_status === "in_progress";
            const offInProgress = status.offboarding_status === "in_progress";
            return (
              <div key={u.id} className={`grid-cell p-6 group relative ${u.disabled ? "opacity-50" : ""}`} data-testid={`directory-card-${u.id}`}>
                {(acts || canViewDocs || (canInitiateReview && u.id !== user.id && !u.disabled)) && (
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 flex gap-1">
                    {canViewDocs && (
                      <button onClick={() => setDocsUserId(u.id)} className="p-1 text-muted-foreground hover:text-[#002FA7]" data-testid={`view-docs-${u.id}`} title="View documents">
                        <FileText size={14} />
                      </button>
                    )}
                    {canInitiateReview && u.id !== user.id && !u.disabled && (
                      <button onClick={() => setReviewUser(u)} className="p-1 text-muted-foreground hover:text-[#002FA7]" data-testid={`start-review-${u.id}`} title="Start performance review">
                        <ClipboardText size={14} />
                      </button>
                    )}
                    {acts && (
                      <>
                        <button onClick={() => setEditingUser(u)} className="p-1 text-muted-foreground hover:text-[#002FA7]" data-testid={`edit-user-${u.id}`} title="Edit">
                          <PencilSimple size={14} />
                        </button>
                        {canDisable && (
                          <button onClick={() => toggleDisabled(u)} className="p-1 text-muted-foreground hover:text-[#002FA7]" data-testid={`toggle-user-${u.id}`} title={u.disabled ? "Enable" : "Disable"}>
                            {u.disabled ? <CheckCircle size={14} /> : <Prohibit size={14} />}
                          </button>
                        )}
                        {canResetPw && u.id !== user.id && (
                          <button onClick={() => setResetPwUser(u)} className="p-1 text-muted-foreground hover:text-[#002FA7]" data-testid={`reset-pw-${u.id}`} title="Reset password">
                            <Key size={14} />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => remove(u.id)} className="p-1 text-muted-foreground hover:text-[#FF3B30]" data-testid={`delete-user-${u.id}`} title="Delete">
                            <Trash size={14} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
                {avatarUrl ? (
                  <img src={avatarUrl} alt={u.name} className="w-16 h-16 object-cover mb-4" />
                ) : (
                  <div className="w-16 h-16 bg-[#0A0A0A] text-white flex items-center justify-center font-display text-2xl font-black mb-4">
                    {u.name?.[0]?.toUpperCase() || "U"}
                  </div>
                )}
                <div className="font-display text-lg font-bold leading-tight">
                  {u.name} {u.disabled && <span className="text-xs text-[#FF3B30] font-mono-mini">· OFFBOARDED</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{u.position || "—"}</div>
                <div className="text-xs text-muted-foreground font-mono-mini mt-1 truncate">{u.email}</div>
                <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex flex-wrap gap-1">
                    {displayRoles.map(r => (
                      <RoleChip key={r} roleKey={r} allRoles={allRoles} />
                    ))}
                  </div>
                  {u.department && <span className="text-xs text-muted-foreground">{u.department}</span>}
                </div>
                {/* Onboarding/offboarding status badges */}
                {(onbInProgress || offInProgress || onbComplete) && (
                  <div className="mt-2 flex items-center gap-1 flex-wrap">
                    {onbComplete && (
                      <span className="text-[9px] font-mono-mini px-1.5 py-0.5 bg-green-100 text-[#166534] uppercase flex items-center gap-1">
                        <Rocket size={9} weight="bold" /> Onboarded
                      </span>
                    )}
                    {onbInProgress && (
                      <span className="text-[9px] font-mono-mini px-1.5 py-0.5 bg-[#F59E0B] text-white uppercase flex items-center gap-1">
                        <Rocket size={9} weight="bold" /> Onboarding
                      </span>
                    )}
                    {offInProgress && (
                      <span className="text-[9px] font-mono-mini px-1.5 py-0.5 bg-[#FF3B30] text-white uppercase flex items-center gap-1">
                        <DoorOpen size={9} weight="bold" /> Offboarding
                      </span>
                    )}
                  </div>
                )}
                {hrNames.length > 0 && (
                  <div className="mt-2 text-[10px] font-mono-mini text-muted-foreground flex items-center gap-1" data-testid={`assigned-hr-${u.id}`}>
                    <IdentificationBadge size={10} weight="bold" /> HR: {hrNames.join(", ")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {docsUserId && (
        <UserDocumentsModal userId={docsUserId} onClose={() => setDocsUserId(null)} onDataChanged={() => { load(); loadStatuses(); }} />
      )}

      <BulkImportUsersDialog
        open={showBulkImport}
        onOpenChange={setShowBulkImport}
        onImported={() => load()}
      />

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white border border-border w-full max-w-md p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-user-dialog">
            <div className="eyebrow mb-2">Add</div>
            <h2 className="font-display text-2xl font-bold mb-6">New team member</h2>
            <form onSubmit={create} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-2">Name</label>
                  <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="user-name-input" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Role</label>
                  <select required value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="w-full border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="user-role-select">
                    <option value="" disabled>Select role</option>
                    {allowedRoles.map(rk => {
                      const r = allRoles.find(x => x.key === rk);
                      return <option key={rk} value={rk}>{r?.label || rk}</option>;
                    })}
                  </select>
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-2">Email</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="user-email-input" />
              </div>
              <div>
                <label className="eyebrow block mb-2">Password</label>
                <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="user-password-input" />
                <PasswordStrengthMeter value={form.password} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-2">Department</label>
                  <input value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="user-dept-input" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Position</label>
                  <input value={form.position} onChange={(e) => setForm({...form, position: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="user-position-input" />
                </div>
              </div>
              {error && <div className="text-sm text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
                <button type="submit" className="flex-1 btn-primary" data-testid="submit-user">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          allUsers={users}
          currentUser={user}
          can={can}
          onClose={() => setEditingUser(null)}
          onSaved={load}
        />
      )}
      {resetPwUser && (
        <ResetPasswordModal
          target={resetPwUser}
          onClose={() => setResetPwUser(null)}
          onDone={() => setResetPwUser(null)}
        />
      )}
      {reviewUser && (
        <StartReviewModal
          target={reviewUser}
          onClose={() => setReviewUser(null)}
          onDone={() => setReviewUser(null)}
        />
      )}
    </div>
  );
}


function ResetPasswordModal({ target, onClose, onDone }) {
  const [mode, setMode] = useState("email");
  const [manualPw, setManualPw] = useState("");
  const [pwValid, setPwValid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { mode };
      if (mode === "set") payload.password = manualPw;
      const r = await api.post(`/users/${target.id}/reset-password`, payload);
      setResult(r.data);
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="reset-pw-modal">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Reset password</div>
            <h3 className="font-display text-lg font-black">{target.name}</h3>
            <div className="text-xs text-muted-foreground">{target.email}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-4">
          {err && <div className="text-xs text-red-700 border border-red-500 bg-red-50 px-3 py-2">{err}</div>}
          {result ? (
            <div className="space-y-3">
              <div className="border border-green-500 bg-green-50 text-green-800 p-3 text-sm">
                {result.message || "Done."}
              </div>
              {result.password && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Generated password (shown once)</div>
                  <div className="font-mono text-lg break-all border border-border p-3 bg-muted select-all" data-testid="reset-generated-pw">{result.password}</div>
                  <button onClick={() => navigator.clipboard.writeText(result.password)} className="mt-2 px-3 py-1 text-xs border border-border hover:bg-muted">Copy</button>
                </div>
              )}
              {result.reset_link && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Reset link (also emailed if configured)</div>
                  <div className="font-mono text-xs break-all border border-border p-3 bg-muted select-all">{result.reset_link}</div>
                  <button onClick={() => navigator.clipboard.writeText(result.reset_link)} className="mt-2 px-3 py-1 text-xs border border-border hover:bg-muted">Copy link</button>
                </div>
              )}
              <div className="text-xs text-muted-foreground">All active sessions on this account have been signed out.</div>
            </div>
          ) : (
            <>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">How to reset?</div>
                <div className="space-y-2">
                  {[
                    { key: "email", label: "Email a reset link", desc: "Send the user a one-time link (valid 60 min). Falls back to a shareable link if email isn't configured." },
                    { key: "generate", label: "Generate a random password", desc: "Immediately sets a strong random password. Share it securely with the user." },
                    { key: "set", label: "Set a specific password", desc: "Type a password to force-set for the user." },
                  ].map(opt => (
                    <label key={opt.key} className={`block border p-3 cursor-pointer ${mode === opt.key ? "border-[#002FA7] bg-blue-50" : "border-border hover:border-muted-foreground"}`}>
                      <div className="flex items-start gap-2">
                        <input type="radio" name="reset-mode" value={opt.key} checked={mode === opt.key} onChange={() => setMode(opt.key)} className="mt-1" data-testid={`reset-mode-${opt.key}`} />
                        <div className="flex-1">
                          <div className="text-sm font-semibold">{opt.label}</div>
                          <div className="text-xs text-muted-foreground">{opt.desc}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              {mode === "set" && (
                <div>
                  <label className="eyebrow block mb-2">New password</label>
                  <input type="password" value={manualPw} onChange={(e) => setManualPw(e.target.value)} className="w-full border border-border px-3 py-2 text-sm" data-testid="reset-manual-pw" />
                  <PasswordStrengthMeter value={manualPw} onValidityChange={setPwValid} />
                </div>
              )}
            </>
          )}
        </div>
        {!result && (
          <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-border">Cancel</button>
            <button
              onClick={submit}
              disabled={busy || (mode === "set" && !pwValid)}
              className="btn-primary"
              data-testid="reset-pw-submit"
            >
              {busy ? "Working…" : "Reset password"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


function StartReviewModal({ target, onClose, onDone }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [reviewerId, setReviewerId] = useState("");
  const today = new Date();
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(today.getMonth() - 6);
  const [periodStart, setPeriodStart] = useState(sixMonthsAgo.toISOString().slice(0, 10));
  const [periodEnd, setPeriodEnd] = useState(today.toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/users");
        setUsers(r.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const reviewers = users.filter(u => !u.disabled && u.id !== target.id && u.role !== "employee");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/performance-reviews", {
        subject_user_id: target.id,
        reviewer_id: reviewerId || undefined,
        period_start: periodStart,
        period_end: periodEnd,
        notes: notes.trim(),
      });
      onDone?.();
      navigate(`/performance/${r.data.id}`);
    } catch (e) {
      setErr(formatAxiosError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white border border-border shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="start-review-modal">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="eyebrow text-[10px] mb-1">Start performance review</div>
            <div className="font-display text-lg font-bold">{target.name}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-muted-foreground">
            Starting a review will notify <b>{target.name}</b> to submit their self-assessment first.
            When they submit, the reviewer will be notified to complete their side.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1 text-[10px]">Period start</label>
              <input
                type="date"
                value={periodStart}
                onChange={e => setPeriodStart(e.target.value)}
                className="w-full border border-border px-3 py-2 text-sm"
                data-testid="review-period-start"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1 text-[10px]">Period end</label>
              <input
                type="date"
                value={periodEnd}
                onChange={e => setPeriodEnd(e.target.value)}
                className="w-full border border-border px-3 py-2 text-sm"
                data-testid="review-period-end"
              />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-1 text-[10px]">Reviewer (defaults to you)</label>
            <select
              value={reviewerId}
              onChange={e => setReviewerId(e.target.value)}
              className="w-full border border-border px-3 py-2 text-sm bg-white"
              data-testid="review-reviewer-select"
            >
              <option value="">Me (default)</option>
              {reviewers.map(u => (
                <option key={u.id} value={u.id}>{u.name} · {u.role}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="eyebrow block mb-1 text-[10px]">Note (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Any context for the subject — focus areas, goals set at the last review, etc."
              className="w-full border border-border px-3 py-2 text-sm"
              data-testid="review-notes"
            />
          </div>
          {err && <div className="border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] px-3 py-2 text-xs">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !periodStart || !periodEnd}
            className="bg-[#002FA7] text-white px-3 py-2 text-sm font-bold hover:bg-[#001a6b] disabled:opacity-50 flex items-center gap-2"
            data-testid="review-start-confirm"
          >
            <ClipboardText size={12} weight="bold" /> {busy ? "Starting…" : "Start review"}
          </button>
        </div>
      </div>
    </div>
  );
}

